import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import {
  Phone,
  Mail,
  Building2,
  Calendar,
  Plus,
  CheckCircle2,
  Circle,
  Trash2,
  Edit,
  FileText,
  Clock,
} from 'lucide-react';
import { format, parseISO, isPast, isFuture } from 'date-fns';

export default function ContactDetail({ contact, onContactUpdated, onContactDeleted }) {
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [editedContact, setEditedContact] = useState(contact);
  const [newTask, setNewTask] = useState({ title: '', due_date: '', description: '' });
  const [newNote, setNewNote] = useState('');
  const [showTaskForm, setShowTaskForm] = useState(false);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tasks?contact_id=${contact.id}`);
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    }
  }, [contact.id]);

  const fetchNotes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notes?contact_id=${contact.id}`);
      setNotes(response.data);
    } catch (error) {
      toast.error('Failed to load notes');
    }
  }, [contact.id]);

  useEffect(() => {
    setEditedContact(contact);
    fetchTasks();
    fetchNotes();
  }, [contact, fetchTasks, fetchNotes]);

  const handleUpdateContact = async () => {
    try {
      const response = await axios.put(`${API}/contacts/${contact.id}`, editedContact);
      onContactUpdated(response.data);
      setIsEditingContact(false);
      toast.success('Contact updated');
    } catch (error) {
      toast.error('Failed to update contact');
    }
  };

  const handleDeleteContact = async () => {
    if (!window.confirm('Delete this contact?')) return;

    try {
      await axios.delete(`${API}/contacts/${contact.id}`);
      onContactDeleted(contact.id);
      toast.success('Contact deleted');
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTask.title.trim()) return;

    try {
      const response = await axios.post(`${API}/tasks`, {
        ...newTask,
        contact_id: contact.id,
      });
      setTasks([response.data, ...tasks]);
      setNewTask({ title: '', due_date: '', description: '' });
      setShowTaskForm(false);
      toast.success('Follow-up added');
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleToggleTask = async (task) => {
    try {
      const response = await axios.put(`${API}/tasks/${task.id}`, {
        completed: !task.completed,
      });
      setTasks(tasks.map((t) => (t.id === task.id ? response.data : t)));
      toast.success(task.completed ? 'Reopened' : 'Completed');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      setTasks(tasks.filter((t) => t.id !== taskId));
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      const response = await axios.post(`${API}/notes`, {
        content: newNote,
        contact_id: contact.id,
      });
      setNotes([response.data, ...notes]);
      setNewNote('');
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/notes/${noteId}`);
      setNotes(notes.filter((n) => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM dd, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const isTaskOverdue = (task) => {
    if (!task.due_date || task.completed) return false;
    try {
      const dueDate = parseISO(task.due_date);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const due = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
      return due < today;
    } catch {
      return false;
    }
  };

  const isTaskToday = (task) => {
    if (!task.due_date || task.completed) return false;
    try {
      const dueDate = parseISO(task.due_date);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const due = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
      return due.getTime() === today.getTime();
    } catch {
      return false;
    }
  };

  const getTaskPriorityLabel = (task) => {
    if (task.completed) return null;
    if (isTaskOverdue(task)) {
      return <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] font-semibold px-2">OVERDUE</Badge>;
    }
    if (isTaskToday(task)) {
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] font-semibold px-2">TODAY</Badge>;
    }
    if (task.due_date) {
      return <Badge variant="outline" className="text-[10px] font-medium px-2">UPCOMING</Badge>;
    }
    return null;
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-8 border-b border-gray-100 bg-gradient-to-b from-white to-[#F9F9F7]">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1A4D2E] to-[#14532D] flex items-center justify-center text-white text-xl font-bold shadow-lg">
              {getInitials(contact.name)}
            </div>
            <div>
              {isEditingContact ? (
                <Input
                  data-testid="edit-contact-name-input"
                  value={editedContact.name}
                  onChange={(e) => setEditedContact({ ...editedContact, name: e.target.value })}
                  className="text-2xl font-bold h-auto px-2 py-1 mb-2"
                />
              ) : (
                <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-2">
                  {contact.name}
                </h2>
              )}
              <Badge
                variant="outline"
                className={`${
                  contact.status === 'active'
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-gray-50 text-gray-600 border-gray-200'
                }`}
              >
                {contact.status}
              </Badge>
            </div>
          </div>
          <div className="flex gap-2">
            {isEditingContact ? (
              <>
                <Button
                  data-testid="save-contact-button"
                  onClick={handleUpdateContact}
                  size="sm"
                  className="rounded-full bg-[#1A4D2E] hover:bg-[#14532D]"
                >
                  Save
                </Button>
                <Button
                  onClick={() => {
                    setIsEditingContact(false);
                    setEditedContact(contact);
                  }}
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  data-testid="edit-contact-button"
                  onClick={() => setIsEditingContact(true)}
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  data-testid="delete-contact-button"
                  onClick={handleDeleteContact}
                  size="sm"
                  variant="outline"
                  className="rounded-full text-red-600 hover:bg-red-50 border-red-200"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        {isEditingContact ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Email</label>
              <Input
                data-testid="edit-contact-email-input"
                value={editedContact.email || ''}
                onChange={(e) => setEditedContact({ ...editedContact, email: e.target.value })}
                placeholder="email@example.com"
                className="h-10"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Phone</label>
              <Input
                data-testid="edit-contact-phone-input"
                value={editedContact.phone || ''}
                onChange={(e) => setEditedContact({ ...editedContact, phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="h-10"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Company</label>
              <Input
                data-testid="edit-contact-company-input"
                value={editedContact.company || ''}
                onChange={(e) => setEditedContact({ ...editedContact, company: e.target.value })}
                placeholder="Company Name"
                className="h-10"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Status</label>
              <select
                data-testid="edit-contact-status-select"
                value={editedContact.status}
                onChange={(e) => setEditedContact({ ...editedContact, status: e.target.value })}
                className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-white"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {contact.email && (
              <div className="flex items-center gap-2 text-gray-600">
                <Mail className="w-4 h-4" />
                <span className="text-sm">{contact.email}</span>
              </div>
            )}
            {contact.phone && (
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="w-4 h-4" />
                <span className="text-sm">{contact.phone}</span>
              </div>
            )}
            {contact.company && (
              <div className="flex items-center gap-2 text-gray-600">
                <Building2 className="w-4 h-4" />
                <span className="text-sm">{contact.company}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-gray-400">
              <Calendar className="w-4 h-4" />
              <span className="text-sm">Added {formatDate(contact.created_at)}</span>
            </div>
          </div>
        )}
      </div>

      <Tabs defaultValue="tasks" className="flex-1 flex flex-col">
        <TabsList className="mx-8 mt-6 w-fit bg-gray-100">
          <TabsTrigger value="tasks" className="data-[state=active]:bg-white">
            Follow-Ups ({tasks.length})
          </TabsTrigger>
          <TabsTrigger value="notes" className="data-[state=active]:bg-white">
            Notes ({notes.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="flex-1 px-8 pb-8 mt-4">
          <div className="space-y-4">
            {!showTaskForm ? (
              <Button
                data-testid="add-task-button"
                onClick={() => setShowTaskForm(true)}
                variant="outline"
                className="w-full h-11 rounded-lg border-dashed border-2 hover:border-[#1A4D2E] hover:bg-[#F0FDF4]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Schedule Follow-Up
              </Button>
            ) : (
              <form onSubmit={handleCreateTask} className="p-4 rounded-xl border-2 border-[#1A4D2E] bg-[#F0FDF4] space-y-3">
                <Input
                  data-testid="task-title-input"
                  placeholder="What do you need to remember?"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  className="h-11 bg-white"
                  autoFocus
                />
                <Textarea
                  data-testid="task-description-input"
                  placeholder="Add details (optional)"
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  className="min-h-[80px] bg-white"
                />
                <Input
                  data-testid="task-due-date-input"
                  type="datetime-local"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  className="h-11 bg-white"
                />
                <div className="flex gap-2">
                  <Button
                    data-testid="create-task-button"
                    type="submit"
                    size="sm"
                    className="rounded-full bg-[#1A4D2E] hover:bg-[#14532D]"
                  >
                    Save Follow-Up
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setShowTaskForm(false);
                      setNewTask({ title: '', due_date: '', description: '' });
                    }}
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            <ScrollArea className="h-[calc(100vh-32rem)]">
              <div className="space-y-3">
                {tasks.length === 0 ? (
                  <div className="text-center py-12 text-gray-400 px-6">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-base font-medium text-gray-700 mb-1">No follow-ups yet</p>
                    <p className="text-sm text-gray-500">Schedule one to stay on track.</p>
                  </div>
                ) : (
                  tasks.map((task) => (
                    <div
                      key={task.id}
                      data-testid={`task-card-${task.id}`}
                      className={`p-4 rounded-lg border bg-white shadow-sm hover:shadow-md transition-all flex items-start gap-3 ${
                        task.completed
                          ? 'border-gray-100 opacity-60'
                          : isTaskOverdue(task)
                          ? 'border-red-200 bg-red-50'
                          : 'border-gray-100'
                      }`}
                    >
                      <Checkbox
                        data-testid={`task-checkbox-${task.id}`}
                        checked={task.completed}
                        onCheckedChange={() => handleToggleTask(task)}
                        className="mt-1 task-checkbox"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4
                            className={`font-medium text-gray-900 flex-1 ${
                              task.completed ? 'line-through text-gray-400' : ''
                            }`}
                          >
                            {task.title}
                          </h4>
                          {getTaskPriorityLabel(task)}
                        </div>
                        {task.description && (
                          <p className="text-sm text-gray-500 mt-1">{task.description}</p>
                        )}
                        {task.due_date && (
                          <div
                            className={`flex items-center gap-1 mt-2 text-xs ${
                              isTaskOverdue(task)
                                ? 'text-red-600 font-medium'
                                : 'text-gray-500'
                            }`}
                          >
                            <Clock className="w-3 h-3" />
                            Due: {formatDateTime(task.due_date)}
                          </div>
                        )}
                      </div>
                      <Button
                        data-testid={`delete-task-button-${task.id}`}
                        onClick={() => handleDeleteTask(task.id)}
                        size="sm"
                        variant="ghost"
                        className="text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </TabsContent>

        <TabsContent value="notes" className="flex-1 px-8 pb-8 mt-4">
          <div className="space-y-4">
            <form onSubmit={handleCreateNote} className="space-y-3">
              <Textarea
                data-testid="note-content-input"
                placeholder="Add a note about this contact..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="min-h-[100px] rounded-lg"
              />
              <Button
                data-testid="create-note-button"
                type="submit"
                disabled={!newNote.trim()}
                size="sm"
                className="rounded-full bg-[#1A4D2E] hover:bg-[#14532D]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Save Note
              </Button>
            </form>

            <ScrollArea className="h-[calc(100vh-28rem)]">
              <div className="space-y-4">
                {notes.length === 0 ? (
                  <div className="text-center py-12 text-gray-400 px-6">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-base font-medium text-gray-700 mb-1">No notes yet</p>
                    <p className="text-sm text-gray-500">Add context about this contact.</p>
                  </div>
                ) : (
                  notes.map((note) => (
                    <div
                      key={note.id}
                      data-testid={`note-card-${note.id}`}
                      className="p-4 rounded-lg border border-gray-100 bg-white shadow-sm"
                    >
                      <div className="flex justify-between items-start gap-3">
                        <p className="text-gray-700 flex-1">{note.content}</p>
                        <Button
                          data-testid={`delete-note-button-${note.id}`}
                          onClick={() => handleDeleteNote(note.id)}
                          size="sm"
                          variant="ghost"
                          className="text-gray-400 hover:text-red-600 flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">{formatDateTime(note.created_at)}</p>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}